import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Alert, ActivityIndicator } from 'react-native';
import { Button, TextInput, Surface, Text } from 'react-native-paper';
import { DatePickerInput } from 'react-native-paper-dates';
import { pt, registerTranslation } from 'react-native-paper-dates';
import { Picker } from '@react-native-picker/picker';
import { useNavigation, useRoute } from '@react-navigation/native';
import { supabase } from '../services/supabase';
import { format, parseISO } from 'date-fns';
import api, { apiService } from '../services/api';

// Registra a tradução em português
registerTranslation('pt', pt);

interface StatusCobranca {
  S_EXIGEDATA: string;
  S_STATUSCOB: string;
  I_PRIORIDADE: string;
  I_DIASRETORNO: string;
  I_CODSTATUSCOB: string;
}

export function AgendamentoCobranca() {
  const navigation = useNavigation();
  const route = useRoute<any>();
  const { titulo } = route.params;

  const [data, setData] = useState<Date | undefined>(undefined);
  const [status, setStatus] = useState('');
  const [observacao, setObservacao] = useState('');
  const [statusList, setStatusList] = useState<StatusCobranca[]>([]);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState<{ empresa: string; codusur: string } | null>(null);

  const isFormValid = () => {
    return data !== undefined && status !== '';
  };

  const fetchUserAndStatusData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data: userDataResponse, error: userError } = await supabase
        .from('usuarios')
        .select('empresa, codusur')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;
      if (!userDataResponse?.empresa) throw new Error('Empresa não encontrada');

      const { data, error } = await supabase
        .from('listasconsultaapi')
        .select('data')
        .eq('rotaapi', 'FINANCEIROstatuscob')
        .eq('codempresa', userDataResponse.empresa)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;

      if (data && data[0]?.data) {
        setStatusList(data[0].data);
      }

      setUserData(userDataResponse);
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      Alert.alert('Erro', 'Não foi possível carregar os dados necessários');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserAndStatusData();
  }, []);

  const handleSave = async () => {
    if (!isFormValid()) {
      Alert.alert('Erro', 'Por favor, preencha todos os campos obrigatórios corretamente');
      return;
    }

    try {
      const selectedStatus = statusList.find(s => s.I_CODSTATUSCOB === status);
      if (!selectedStatus) throw new Error('Status não selecionado');

      if (!userData) throw new Error('Dados do usuário não encontrados');

      const { data: clientData, error: clientError } = await supabase
        .from('listasconsultaapi')
        .select('data')
        .eq('rotaapi', 'FINANCEIROListaClienteDeb')
        .eq('codempresa', userData.empresa)
        .order('created_at', { ascending: false })
        .limit(1);

      if (clientError) throw clientError;
      if (!clientData?.[0]?.data) throw new Error('Dados do cliente não encontrados');

      const cliente = clientData[0].data.find((c: { I_CODCLI: string; S_TELCOB: string }) => c.I_CODCLI === titulo.I_CODCLI);
      if (!cliente) throw new Error('Cliente não encontrado');

      // Formatando a data para DD/MM/YYYY
      const dataFormatada = data ? format(data, 'dd/MM/yyyy') : '';
      
      const requestData1 = {
        codfunc: userData.codusur,
        codcli: titulo.I_CODCLI,
        telcob: cliente.S_TELCOB,
        obs1: observacao,
        statuscob: selectedStatus.S_STATUSCOB,
        INSERESTATUSCOB: 'N',
        codstatuscob: status,
        DATARETORNO: dataFormatada
      };
      
      console.log('Data formatada:', dataFormatada);

      console.log('Dados para primeiro endpoint:', requestData1);
      if (!userData) throw new Error('Dados do usuário não encontrados');
      await apiService.insertHistoricoCob(JSON.stringify(requestData1), userData.empresa);

      const requestData2 = {
        codfilial: titulo.I_CODFILIAL,
        numtransvenda: titulo.I_NUMTRANSVENDA,
        duplic: titulo.I_DUPLIC,
        prest: titulo.S_PREST,
        statuscob: selectedStatus.S_STATUSCOB,
        INSERESTATUSCOB: 'N',
        codstatuscob: status,
        codcli: titulo.I_CODCLI,
        DATARETORNO: dataFormatada
      };

      console.log('Dados para segundo endpoint:', requestData2);
      await apiService.insertHistoricoCobi(JSON.stringify(requestData2), userData.empresa);

      Alert.alert('Sucesso', 'Agendamento realizado com sucesso');
      navigation.goBack();
    } catch (error) {
      console.error('Erro no agendamento:', error);
      Alert.alert('Erro', 'Não foi possível realizar o agendamento');
    }
  };

  if (loading) {
    return (
      <View style={styles.loading}>
        <ActivityIndicator size="large" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Surface style={styles.content}>
        <ScrollView>
          <View style={styles.tituloInfo}>
            <Text style={styles.label}>Duplicata/Prestação:</Text>
            <Text style={styles.value}>{titulo.S_DUPLICPREST}</Text>
            
            <Text style={styles.label}>Valor:</Text>
            <Text style={styles.value}>
              {new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL'
              }).format(titulo.F_VALOR)}
            </Text>
            
            <Text style={styles.label}>Vencimento:</Text>
            <Text style={styles.value}>
              {format(parseISO(titulo.D_DTVENC), 'dd/MM/yyyy')}
            </Text>
          </View>

          <View style={styles.field}>
            <Text>Data do Próximo Contato</Text>
            <DatePickerInput
              locale="pt"
              label="Data"
              value={data}
              onChange={(date) => setData(date)}
              inputMode="start"
              style={styles.input}
              mode="outlined"
            />
          </View>

          <View style={styles.field}>
            <Text>Status da Cobrança</Text>
            <View style={styles.pickerContainer}>
              <Picker
                selectedValue={status}
                onValueChange={(itemValue) => setStatus(itemValue)}
                style={styles.picker}
              >
                <Picker.Item label="Selecione um status..." value="" />
                {statusList && statusList.map(item => (
                  <Picker.Item
                    key={item.I_CODSTATUSCOB}
                    label={item.S_STATUSCOB}
                    value={item.I_CODSTATUSCOB}
                  />
                ))}
              </Picker>
            </View>
          </View>

          <View style={styles.field}>
            <Text>Observação</Text>
            <TextInput
              mode="outlined"
              value={observacao}
              onChangeText={setObservacao}
              multiline
              numberOfLines={4}
              style={styles.textArea}
            />
          </View>

          <View style={styles.buttons}>
            <Button
              mode="outlined"
              onPress={() => navigation.goBack()}
              style={styles.button}
            >
              Cancelar
            </Button>
            <Button
              mode="contained"
              onPress={handleSave}
              style={styles.button}
              disabled={!isFormValid()}
            >
              Gravar
            </Button>
          </View>
        </ScrollView>
      </Surface>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
    backgroundColor: '#f5f5f5',
  },
  content: {
    padding: 16,
    borderRadius: 8,
    flex: 1,
  },
  tituloInfo: {
    backgroundColor: '#f0f0f0',
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    color: '#666',
    marginTop: 8,
  },
  value: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  field: {
    marginBottom: 16,
  },
  input: {
    marginTop: 8,
  },
  pickerContainer: {
    marginTop: 8,
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 4,
    backgroundColor: '#fff',
  },
  picker: {
    height: 50,
  },
  textArea: {
    marginTop: 8,
  },
  buttons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 24,
    marginBottom: 24,
  },
  button: {
    flex: 1,
    marginHorizontal: 8,
  },
  loading: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
